import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import styled from 'styled-components';

import { Button } from '@soluto-private/mx-asurion-ui-react';

import { logger } from '@service-initiation/shared-clients-osp-logger';
import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import {
  topImageDesktop,
  topImageMobile,
} from '@service-initiation/shared-ui-assets';
import {
  breakpoints,
  useIsDesktop,
} from '@service-initiation/shared-ui-breakpoints';
import { CheckCircleIcon } from '@service-initiation/shared-ui-check-circle-icon';
import { PageContainer } from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import { useLeadIdValue } from '@service-initiation/tech-care-data-access-store';
import { cancelAppointment } from '@service-initiation/tech-care-data-access-tech-care-services';

const TopImage = styled.img`
  width: 100%;
  margin: 32px 0;

  ${breakpoints.tablet} {
    border-radius: 8px;
  }
`;

const Title = styled.div`
  font-size: 32px;
  font-style: normal;
  font-weight: 300;
  line-height: 120%; /* 38.4px */
  margin: 16px 0;
`;
const SubTitle = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;
const WantToReschedule = styled.div`
  margin-bottom: 16px;
`;
const TopContainer = styled.div`
  margin: 16px 16px;
  margin-bottom: 0px;

  ${breakpoints.tablet} {
    margin: 16px 0px;
    margin-top: 48px;
  }
`;
const BottomContainer = styled.div`
  margin: 0px 16px;

  ${breakpoints.tablet} {
    margin: 0px 0px;
  }
`;
const ScheduleButton = styled(Button)`
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 26px */

  && {
    width: 100%;
  }
`;

const ColumnPageContainer = styled(PageContainer)`
  && {
    flex-direction: column;
    ${breakpoints.tablet} {
      margin: 0px 48px;
    }
  }
`;

const TEXTS = TranslationEntries.Repairs.Cancellation.CancelCompletePage;

export const ConfirmedCancellationPage: FC = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const leadId = useLeadIdValue();
  const { t } = useTranslation();
  const { dispatchClickEvent } = useAnalytics();
  const { ref } = useInViewAnalytics('ConfirmedCancellationPage');

  const hasRunOnce = useRef(false);
  const { loading: isLoading } = useAsync(async () => {
    if (leadId) {
      if (!hasRunOnce.current) {
        try {
          return await cancelAppointment(leadId);
        } catch (_e) {
          const err = _e as Error;
          err.message = 'Error occurred while trying to cancel the appointment';
          logger.error(err);
        }
        hasRunOnce.current = true;
      }
    } else {
      navigate(Route.CancelAppointment);
      const error = new Error('No LeadId');
      logger.error(error);
      throw error;
    }
  }, []);

  return isLoading ? (
    <StepContentSkeleton />
  ) : (
    <ColumnPageContainer ref={ref}>
      <TopContainer>
        <CheckCircleIcon />
        <Title>{t(TEXTS.Title)}</Title>
        <SubTitle>{t(TEXTS.SubTitle)}</SubTitle>
      </TopContainer>
      <TopImage
        alt="Cancellation image"
        data-testid="cancellation-top-image"
        src={isDesktop ? topImageDesktop : topImageMobile}
      />
      <BottomContainer>
        <WantToReschedule>{t(TEXTS.AskReschedule)}</WantToReschedule>
        <ScheduleButton
          onClick={() => {
            dispatchClickEvent('CompletedCancellationPageReschedule', {
              Cta: 'CompletedCancellationPageReschedule',
            });
            navigate(Route.Landing);
          }}
        >
          {t(TEXTS.ConfirmReschedule)}
        </ScheduleButton>
      </BottomContainer>
    </ColumnPageContainer>
  );
};
